import {action, computed, observable, runInAction, _, jsonComputedOptions, stringify} from '@api'
import {FILTER_UNASSIGNED_ID, UNASSIGNED, GroupByGroupStore, utils, AssortmentCardStore} from '@modules'
import {ui, onSiteError, StoresInstance} from '@stores'
import {VipLocalStore} from '@modules'
import {HitsResult} from '@stores/elasticsearch'
import {authStore, removeUndefinedsNested} from '@utils'
import {autorun, toJS, trace, reaction} from 'mobx'

export const EMPTY_STRING_VALUE = '[empty value]'
export const NULL_KEY_VALUE = '[null value]'

interface IAggregateData {
  buckets: Array<{ key, key_as_string?: string, doc_count: number }>
}

export interface IGroupByQueryManagerResult {
  id,
  /** Must be unique for react components - Due to things like subtitles we can end up with the same merchProduct showing up multiple times */
  key,
  keys?
  title
  count?: number
  doc_count?: number
  offline_count?: number
  offlineItems?: Array<{ _id: string } & any>
  otherCounts?: { [id: string]: number }
  _source?: any
  aggs?: { [field: string]: IAggregateData & { hits: HitsResult } }
  esItem?: { hits: Partial<HitsResult> }
  /**
   * Special grouping for unassigned items, may include offline items
   */
  isUnassignedGroup?: boolean
}

/**
 * LoadMore Management for children groups - either at the top level of the entire hierarchy, or down some specific path.
 */
export class GroupByGroupsQueryManager extends VipLocalStore {

  constructor(stores: StoresInstance, public parentGroupStore?: GroupByGroupStore, public props?: { assortmentCard?: AssortmentCardStore }) {
    super(stores)
    // console.log(`Construct GroupByGroupsQueryManager`, parentGroupStore)
  }

  @computed get level() {
    return this.parentGroupStore?.props?.level !== undefined ? (1 + this.parentGroupStore?.props?.level) : 0
  }

  @computed get path() {
    return this.parentGroupStore?.pathIncludingSelfKeys ?? []
  }

  /**
   * Will be undefined when we're a leaf node (grandchild)
   */
  @computed get grouping() {
    return this.groupBy.hierarchyGroups[this.level]
  }

  @computed get groupName() { return this.grouping?.groupName}

  @computed get filterGroup() {
    return this.filter.state.groups[this.groupName]
  }

  // Data Loading and Results

  @observable initialLoad: boolean = true
  @observable loading = false
  @observable.ref rawData = observable.array<Array<IGroupByQueryManagerResult>>([], {deep: false})

  @computed get maxItemsAlreadyLoaded() {
    return this.itemsPerClick * (this.timesClicked + 1)
  }

  // We do this work when processing the result of the query
  // @computed get offlineGroupData () {
  //   const {offlineGroups, groupName, rawData} = this,
  //     {_fieldByName} = catalog,
  //     fieldDef = _fieldByName[groupName],
  //     groups = _.flatMap(rawData)
  //   return Object.keys(offlineGroups).reduce((acc, title) => {
  //     const key = title !== "undefined" ? title : UNASSIGNED, boolean = fieldDef?.def?.type === 'Boolean'
  //     const f = groups.find(g => title === "undefined"
  //                               ? boolean ? _.get(g.keys, groupName) === null :  g.title.toLowerCase() === UNASSIGNED : boolean ? _.get(g.keys, groupName).toString() === title : g.title === title)
  //     if (!f) {
  //       acc = [
  //         ...acc, {
  //           id: {
  //             [groupName]: key
  //           },
  //           count: 0,
  //           aggs: undefined,
  //           title,
  //           key: `{${groupName}: ${key}}`,
  //           keys: {
  //             [groupName]: key
  //           },
  //           offline_count: offlineGroups[title]?.length || 0
  //         }
  //       ]
  //     }
  //     return acc
  //   }, [])
  // }

  shuffleEmptyUnassignedToLastOfBucket(data: Array<IGroupByQueryManagerResult>): Array<IGroupByQueryManagerResult> {
    const {strings, groupName} = this
    const unassignedItem = _.remove(data, t => t.keys[groupName] === null)
    const emptyItem = _.remove(data, t => t.keys[groupName] === '')

    let r = data
    emptyItem.length && (r = [...r, ...emptyItem])
    unassignedItem.length && (r = [...r, ...unassignedItem])

    return r
  }

  @computed get data(): Array<{ key } & any> {
    if (!this.grouping) return []

    const {
      unassigned, filterGroup, maxItemsAlreadyLoaded, groupName, grouping: {firstOrderBy, offlineUnassignedItems, applyCustomSort, isNestedField, sectionConfig}, allGroups,
      strings, catalog: {filter, settings: {groupBy: {showEmptyGroups}}}, rawData
    } = this

    let data = _.flatMap(rawData)

    if (!_.isEmpty(data) && isNestedField && sectionConfig?.viewOptions?.field) {

      // Filter out any groups which aren't selected in view options

      const {catalog: {selectedViewOptionsForDynamicSection, filter: {stateFor}}} = this,
        fullPath = `${sectionConfig.path}.${sectionConfig.viewOptions.field}`,
        filterGroup = stateFor(fullPath),
        selectedViewOptions = selectedViewOptionsForDynamicSection(sectionConfig.id)

      if (selectedViewOptions?.size) {
        // This will only match if we are grouping on the dynamic field (secondary name, for example)
        data = data.filter(v => !_.has(v.keys, fullPath) || selectedViewOptions.has(v.keys[fullPath]))
      }

      if (filterGroup?.selected?.size) {
        data = data.filter(v => filterGroup.selected.has(_.get(v.keys, fullPath)))
      }
    }

    const groups = _.keyBy(data, g => g.keys[groupName] === undefined ? _.startCase(UNASSIGNED) : g.keys[groupName])

    //Needs to be rewritten for composite queries
    // if (filterGroup?.selected?.size && hasPerspectives) {
    //   // Filter out groups clientside that are invalid
    //   const {selected} = filterGroup
    //   data = data.filter(v => selected.has(v.key) || (v.key === EMPTY_STRING_VALUE && selected.has('')) || (isJson(v.key) && selected.has(JSON.parse(v.key))))
    // }
    data = _.uniqBy(_.take(data, data.length ? _.min([data.length, maxItemsAlreadyLoaded]) : maxItemsAlreadyLoaded), 'key')

    //console.log({allGroups, rawData: toJS(rawData, {recurseEverything: true}), data: toJS(data, {recurseEverything: true}), groups: toJS(groups, {recurseEverything: true})})

    if (showEmptyGroups && !this.initialLoad) {
      const validGroups = allGroups.filter(g => !groups[g.keys[groupName]] && !groups[g.title] && (!filterGroup || filterGroup.selected.size == 0 || filterGroup.selected.has(g.keys[groupName])))

      data.push(...validGroups.map(g => ({...g, id: g.keys, count: 0, offline_count: 0})))
    }
    //console.log(data)

    // if (this.offlineGroupData?.length) {
    //   data.push(...this.offlineGroupData)
    // }

    if (showEmptyGroups) {
      // Need to custom sort to combine the empty and non-empty groups
      if (firstOrderBy.field == '_key') {
        data = applyCustomSort(_.orderBy(data, d => d.title ?? d.key, firstOrderBy.order))
      }
      if (firstOrderBy.field == '_count') {
        data = _.orderBy<IGroupByQueryManagerResult>(data, a => a.count, [firstOrderBy.order])
      }
    }

    data = this.shuffleEmptyUnassignedToLastOfBucket(data)

    if (unassigned && (!filterGroup?.selected?.size || filterGroup.selected.has(FILTER_UNASSIGNED_ID))) {
      const key = JSON.stringify({[groupName]: null})
      const unassignedIndex = data.findIndex(v => v.key === key)
      let unassignedInfo: IGroupByQueryManagerResult = unassignedIndex != -1 ? data[unassignedIndex] : null
      if (!unassignedInfo) {
        unassignedInfo = {
          key,
          id: key,
          title: this.grouping.fieldDefinition?.def?.nullLabel ?? strings.unassigned,
          count: this.online_unassigned,
          offline_count: offlineUnassignedItems?.length || 0,
          offlineItems: offlineUnassignedItems,
          isUnassignedGroup: true
        }
      }
      else {
        data.splice(unassignedIndex, 1)
      }

      if (firstOrderBy.field == '_key') {
        if (firstOrderBy.order == 'asc') {
          data.push(unassignedInfo)
        } else {
          data = [unassignedInfo, ...data]
        }
      } else if (firstOrderBy.field == '_count') {
        data = _.orderBy<IGroupByQueryManagerResult>([...data, unassignedInfo], a => a.count, [firstOrderBy.order])
      } else {
        data.push(unassignedInfo)
      }
    }

    if (!showEmptyGroups && filter.onlyShowOfflineItems) {
      data = data.filter(d => d.offline_count > 0)
    }

    return data
  }

  /**
   * Total number of groups (ES will only return us the first itemsPerClick of potentially a much larger number of distinct groups - say MerchProducts)
   */
  @observable groupCount = 0

  @observable lastGroupCount : number

  @computed get groupCountOrLastCount() {
    const {groupCount, lastGroupCount} = this
    return !groupCount ? Number(lastGroupCount) : groupCount
  }
  /**
   * Total number of 'items' (products, merchOptionVersions, etc)
   */
  @observable itemCount = 0

  /**
   * Items that don't fall into any of the group by buckets
   */
  @observable online_unassigned = 0

  @computed get unassigned() {
    const {online_unassigned, grouping} = this

    return online_unassigned + (grouping?.offlineUnassigned || 0)
  }

  //endregion

  // region Query ES for Grouped Aggregate Data

  @computed(jsonComputedOptions) get compositeClause() {
    const {grouping, itemsPerClick, groupBy, catalog} = this
    if (!grouping || grouping?.fieldDefinition?.agg == 'exists' || groupBy.hierarchyGroups.some(g => g.isNestedField)) return undefined

    let compositeClause = grouping.compositeGroupsClauseFor({itemsPerClick, missing_bucket: groupBy.pivotView})
    const innerClause = compositeClause ? removeUndefinedsNested({
      composite: {...compositeClause.composite},
      aggs: {
        ...this.compositeCustomSort,
        ...(grouping?.fieldDefinition?.group?.extraAggs ?? {}),
        esItem: grouping.esIndex ? {
          top_hits: {
            size: ui.settings.maxHitsForSpecGroupAssociatedItems,
            _source: [grouping.esFieldRoot]
          }
        } : undefined,
        ids: grouping.esIndex ? {terms: {field: `${grouping.fieldName}.id`}} : undefined
      }
    }) : undefined

    if (innerClause && grouping.library) {
      const {library, def} = grouping
      const dataMap = catalog.dataMapForLibrary(library)
      _.merge(innerClause, def ? {aggs: {items: {top_hits: {size: 1, _source: [`${library.assignTo}.id`, `${library.assignTo}.${dataMap.images}`]}}}} : {})
    }

    if (grouping.isNestedField) {
      // Gives us the count of the main index item instead of the nested count (think total secondaries vs their parent MPVs)
      _.merge(innerClause, {aggs: {parent: {reverse_nested: {}}}})
    }

    return innerClause
  }

  @computed get compositeCustomSort() {
    const {grouping: {firstCustomSort}} = this
    if (firstCustomSort) {
      return {
        custom_sort: {
          bucket_sort: {
            sort: [
              {
                max_score: {
                  order: 'asc'
                }
              }
            ]
          }
        },
        max_score: {
          max: {
            'script': '_score'
          }
        }
      }
    }
    return undefined
  }

  // We don't use server side paging with composite clauses as we can't determine total count
  @computed get compositeClauseAfter() {
    const {compositeClause, after_key, compositeCustomSort, timesClicked} = this

    return compositeClause ? removeUndefinedsNested({composite: {...compositeClause.composite, after: timesClicked ? after_key : undefined}, ..._.omit(compositeClause, ['composite'])}) : undefined
  }

  @computed(jsonComputedOptions) get groupsClause() {
    const {grouping, itemsPerClick, timesClicked, groupCount, compositeClause} = this
    if (!grouping || compositeClause) return undefined
    const r = grouping.groupsClauseFor({itemsPerClick, timesClicked, groupCount})
    return r
  }

  @computed get queryVariables() {
    const {grouping, groupsClause, compositeClause, parentGroupStore, catalog, props} = this
    if (!grouping || grouping.isCatalogItemsGrouping || catalog.itemCount === null) return null
    const {esField, isNestedField} = grouping,
      {catalogItemFilter, catalogConfig} = catalog,
      innerAggs = {
        composite: _.isEmpty(compositeClause?.composite) ? undefined : this.compositeClauseAfter,
        groups: groupsClause,
        count: {cardinality: {field: esField}},
        //itemCount: {value_count: {field: '_id'}},
        unassigned: {
          missing: {field: esField}
        }
        //...catalog.perspectives.filter(p => p.filter).reduce((acc, p) => ({...acc, [`perpsective_${p.id}`]: {filter: p.filter}}), {})
      }

    const pathNestedFilterFields = isNestedField ? _.keys(parentGroupStore?.keysIncludingPath).filter(k => k.startsWith(grouping.sectionConfig.path)) : []

    //console.log(toJS({groupsClause, compositeClause}, {recurseEverything: true}))
    //trace()
    let r = _.cloneDeep(
      removeUndefinedsNested(
        {
          size: 0,
          ..._.pick(catalogItemFilter?.filter, 'runtime_mappings'),
          aggs: isNestedField && grouping?.sectionConfig?.path ? {
            [grouping.sectionConfig.path]: {
              nested: {path: grouping.sectionConfig.path},
              aggs: _.isEmpty(pathNestedFilterFields) ? innerAggs :
                {
                  filtered: {
                    filter: {
                      bool: {
                        filter: pathNestedFilterFields.map(k => ({term: {[k]: parentGroupStore.keysIncludingPath[k]}}))
                      }
                    },
                    aggs: innerAggs
                  }
                }
            }
          } : innerAggs,
          query: grouping.functionScoreTerm ?? catalogItemFilter?.filter?.query
        }))

    if (parentGroupStore) {
      // Make sure to clone otherwise when we push into the array with our parent's terms we actually will modify a shared variable(!)
      const {mustTerms, mustNotTerms} = parentGroupStore, query = r.query?.function_score?.query ?? r.query
      !_.isEmpty(mustTerms) && query.bool.filter.push(...mustTerms)
      !_.isEmpty(mustNotTerms) && query.bool.filter.push({bool: {must_not: mustNotTerms}})
    }

    if (props?.assortmentCard) {
      const {assortmentCard: {mustTerms}} = props
      let query = r.query?.function_score?.query ?? r.query
      query.bool.filter.push(...mustTerms)
    }
    return r
  }

  @observable.ref after_key: any = undefined

  toString = () => {
    const {level, path, groupName} = this
    return level === 0 ? 'Root' : `${path.join(' → ')} -> ${groupName}`
  }

  /**
   * Groups the Pending Items in the current filtered catalog view (reduced by hierarchy, filters, search text, show only expanded toggles, etc)
   * Each bin is a string with JSON serialized text of all the key:values needed to select this specific group.  This is matched up to composite query results for true items
   * Note that items might be offline in groups of which there are no ES rows yet for said group (group has 0 online items)
   * We also may be nested under other complex groups
   */
  @computed get groupedOfflineItems() {
    const {grouping, parentGroupStore, catalog} = this

    if (!grouping) return {}

    let offlineItems = parentGroupStore?.offlineItems ?? catalog.filteredOfflineItems

    const {es, vlp: {hierarchy: {parseHierarchyGroup}}} = this

    if (grouping.def?.type === 'EnumList' && offlineItems) {
      const uniq: Array<string> = [..._.uniq(_.flatMap(offlineItems, grouping.groupName)), null],
        offlineGroups = {}

      uniq.forEach(g => {
        const {fieldDefinition: {field, group}} = grouping
        const filter = _.filter([...offlineItems], item => {
          const {valAsString} = es.readField(item, field, group?.field)
          return g === null ? valAsString === g : valAsString?.includes(g) ?? false
        })
        if (filter.length) {
          const key = stringify({[field]: g})
          if (offlineGroups[key] === undefined) {
            offlineGroups[key] = []
          }
          offlineGroups[key].push(...filter)
        }
      })
      return offlineGroups
    }
    const groups = _.groupBy(offlineItems, item => {
      const groupKey = stringify(
        // Convert the group + subtitle fields into a key field of all required key:values to match
        [
          {
            field: grouping.groupName,
            esField: grouping.esField,
            agg: grouping.fieldDefinition?.agg,
            def: grouping.fieldDefinition?.def
          },
          ...grouping.otherGroupedFields.map(f => ({field: f.field, agg: null, def: catalog._fieldByName[f.field]?.def, esField: null}))
        ]
          .reduce((acc, {esField, field, def, agg}) => {
            let v = es.readField(item, esField ?? field)?.val

            if (!v) {
              const hierarchyGroup = parseHierarchyGroup(field)

              if (hierarchyGroup) {
                const {type, folderDepth} = hierarchyGroup
                switch (type) {
                  case 'plan':
                    v = item.plan?.id
                    break

                  case 'folder':
                    v = item.subCategories?.length > folderDepth ? item.subCategories[folderDepth]?.id : null
                    break

                  case 'collection':
                    const c = item.collection

                    if (c && field === grouping.fieldName && grouping.fieldDefinition?.group?.field) {
                      v = c ? c[grouping.fieldDefinition.group?.field ?? 'name'] : null
                    } else {
                      v = c?.id
                    }

                    break
                }
              }
            }

            const d = agg === 'exists' ? v != null : v

            return {
              ...acc,
              [field]: _.isArray(d) ? _.last(d) : d === null || d === undefined ? def?.type === 'Boolean' ? false : null
                                                                                : d
            }
          }, {}))

      return groupKey
    })
    //console.log(toJS(parentGroupStore?.props?.info?.keys), toJS(unfilteredOfflineItems), toJS(offlineItems), groups)
    return offlineItems ? groups : {}
  }

  @computed get hasFilter() {
    const {filterGroup} = this
    return filterGroup?.selected?.size > 0
  }

  @computed get queryVariablesBody() {
    const {grouping, compositeClause} = this
    let queryVariables = this.queryVariables

    if (!compositeClause && !grouping.isNestedField && grouping.firstOrderBy.field == '_key' && this.timesClicked > 0) { // Not sure how to do paged nested queries, instead just get everything
      const {firstOrderBy: {order}, esField} = grouping
      const maxMinValue = (order === 'asc' ? _.max : _.min)(this.data.map(v => _.get(v.keys, esField)))

      if (maxMinValue !== null) {
        queryVariables = _.cloneDeep(queryVariables)
        queryVariables.query.bool.filter.push({range: {[grouping.esField]: {[order == 'asc' ? 'gt' : 'lt']: maxMinValue}}})
      }
    }

    return queryVariables
  }

  titleFor = (bucket: IGroupByQueryManagerResult) => {
    const {strings, grouping, catalog} = this, {key} = bucket
    let keyValue = key[grouping?.groupName]

    if (keyValue === '' || keyValue === EMPTY_STRING_VALUE) {
      return strings.noValue
    } else if (keyValue === null || keyValue === NULL_KEY_VALUE) {
      return grouping.fieldDefinition?.def?.nullLabel ?? strings.unassigned
    } else {
      if (grouping?.groupDefinition?.labelField && bucket.esItem) {
        keyValue = _.get(_.first(bucket.esItem.hits.hits)?._source, `${grouping.esFieldRoot}.${grouping.groupDefinition.labelField}`)
      }

      return grouping?.fieldDefinition?.group?.titleFor ? grouping?.fieldDefinition.group.titleFor({keys: key}) : grouping?.def ? catalog.attrValueLabelFor({v: keyValue, def: grouping?.def}) : keyValue
    }
  }

  @action transformResults = ({esResult}): Array<IGroupByQueryManagerResult> => {
    const {stores, strings, filter, groupBy, grouping, catalog, compositeClause, groupName, hasFilter, filterGroup, groupedOfflineItems, titleFor} = this

    let {
      aggregations: {
        groups,
        composite,
        filtered,
        // itemCount: {value: itemCount},
        count: {value: totalGroups},
        unassigned: {doc_count: unassigned}
      },
      hits: {total: {value: itemCount}}
    } = esResult
    let transformed: Array<IGroupByQueryManagerResult> = []

    if (composite || (compositeClause && _.size(Object.keys(groupedOfflineItems)) > 0)) {
      let buckets = composite?.buckets ?? []
      this.after_key = composite?.after_key

      if (grouping?.fieldDefinition?.group?.filterResultBuckets) {
        buckets = grouping?.fieldDefinition.group.filterResultBuckets(buckets, stores)
      }

      for (var otherField of (grouping?.otherGroupedFields ?? [])) {
        const f = catalog._fieldByName[otherField.field]
        if (f?.group?.filterResultBuckets) {
          buckets = f.group.filterResultBuckets(buckets, stores)
        }
      }

      // Put the Pending Items in their respective buckets OR create a bucket if one doesn't already exist with online items in it
      Object.keys(groupedOfflineItems).forEach(o => {
        const keyValues = JSON.parse(o),
          offlineItems = groupedOfflineItems[o],
          offline_count = offlineItems.length

        const existingBucket = buckets.find(b => {
          return _.keys(keyValues).every(k => b.key[k] == keyValues[k])
        })

        if (existingBucket) {
          Object.assign(existingBucket, {offline_count, offlineItems})
        } else {
          // No items for this group exist in ES, only offline (local to the user)
          let offlineResult: Partial<IGroupByQueryManagerResult> = {key: keyValues, doc_count: 0, offline_count, offlineItems}

          // YFTS-7754
          if (grouping?.esIndex) {
            offlineResult.esItem = {hits: {hits: [{_source: {[grouping.esFieldRoot]: _.get(_.first(offlineItems), grouping.esFieldRoot)}}]}}
          }

          buckets.push(offlineResult)
        }
      })

      if (buckets?.length) {
        buckets = grouping ? grouping.applyCustomSort(buckets) : buckets
        transformed = buckets.map(bucket => {
          const {key, doc_count, parent, offline_count, offlineItems, ...rest} = bucket
          // const myKey = key[grouping.esField]
          // let safeKey = myKey === '' ? EMPTY_STRING_VALUE : myKey === null ? NULL_KEY_VALUE : myKey
          //console.log({key, path: this.path})
          //if (_.values(key).some(v => v === null)) { return null }

          if (!filter.onlyShowOfflineItems || offline_count > 0) {
            return ({
              key: utils.isJson(key) ? key : stringify(key),
              keys: key,
              id: key,
              title: titleFor(bucket),
              count: parent ? parent.doc_count : doc_count,
              offline_count,
              offlineItems,
              otherCounts: grouping?.isNestedField ? {[grouping?.sectionConfig.id]: doc_count} : {},
              aggs: rest
            })
          }
        }).filter(_.identity)

        if (grouping?.groupDefinition?.filter) {
          transformed = transformed.filter(grouping.groupDefinition.filter)
        }

        if (grouping?.fieldDefinition?.group?.titleFor && grouping?.firstOrderBy.field == '_key') {
          transformed = _.orderBy(transformed, t => t.title)
          if (grouping?.firstOrderBy.order === 'desc') {
            transformed = _.reverse(transformed)
          }
        }

        if (grouping?.firstOrderBy.field == '_count') {
          transformed = _.sortBy(transformed, t => t.count) as Array<IGroupByQueryManagerResult>

          if (grouping?.firstOrderBy.order == 'desc') {
            transformed = transformed.reverse()
          }
        }

        unassigned = 0 // We will get back unassigned group(s) from the composite query

        // For composite groups with subtitles we don't know how many groups there may be (combinations of group + subtitles that produce non-zero lists of board items).  When custom sorted, we need to get ALL rows instead of just
        // a page's worth
        totalGroups = Math.max(totalGroups, transformed.length)
      }

      // Add any missing values if we're in pivot view - dead code
      if (false && groupBy.pivotView /*&& level < 2*/ && !_.isEmpty(transformed)) {
        // Add any missing group values
        const filterValues = filter.groupFor(grouping?.groupName)?.groupedByValue

        if (!_.isEmpty(filterValues) && grouping) {
          const {groupName, groupDefinition: {labelField}} = grouping
          const byGroupValue = _.keyBy<IGroupByQueryManagerResult>(transformed, d => d.keys[groupName])
          //console.log(toJS(data, {recurseEverything: true}))
          transformed = _.keys(filterValues).map(groupValue => {
            return byGroupValue[groupValue] ?? {
              count: 0,
              id: {[groupName]: groupValue},
              key: stringify({[groupName]: groupValue}),
              keys: {[groupName]: groupValue},
              title: groupValue
            }
          })
        }
      }

      /** We may have gotten '' in our last page of grouped data, which was then sorted manually to the bottom of the list, and is now in an invalid place as we have more data */
      // if (transformed.length && transformed[0].key == EMPTY_STRING_VALUE) {
      //   this.transformedEnd = [transformed[0]]
      //   transformed.splice(0, 1)
      // }

    } else if (groups) {  // Used for exists or nested queries since we can't filter nested objects down and combine with a composite clause
      let {buckets, doc_count: exists} = groups
      this.after_key = undefined
      const parentKeys = this.parentGroupStore?.props?.info?.keys ?? {}
      if (buckets) {
        // Add buckets to our result set if we have Pending Items in a group that doesn't exist in ES
        Object.keys(groupedOfflineItems).forEach(k => {
          const parsedKey = JSON.parse(k),
            v = parsedKey[groupName],
            items = groupedOfflineItems[k],
            filtered = hasFilter ? !filterGroup.selected.has(v) : hasFilter

          if (items.length && !filtered) {
            const f = _.find(buckets, bucket => bucket.key[groupName] === v) ?? (v !== null && this.rawData.some(result => result.find(item => item.keys[groupName] === v))) // Only add unassigned to the first result bucket
            if (!f) {
              // This is a fake bucket that should look like the result from a terms query
              buckets.push({
                key: v,
                doc_count: 0,
                offline_count: items.length,
                offlineItems: items,
                id: v,
                title: this.grouping.fieldDefinition?.def?.nullLabel ?? strings.unassigned,
                count: 0,
                isUnassignedGroup: true
              })
            }
          }
        })

        buckets = grouping ? grouping.applyCustomSort(buckets) : buckets
        transformed = buckets.map(({key, offline_count, offlineItems, key_as_string, doc_count, groupData, ...rest}) => {
          if (!filter.onlyShowOfflineItems || offline_count > 0) {
            if (key_as_string === 'false') { key = false } else if (key_as_string === 'true') { key = true }

            let safeKey = key === '' ? EMPTY_STRING_VALUE : key === null ? NULL_KEY_VALUE : key_as_string ? key_as_string : key
            return ({
              keys: {...parentKeys, [groupName]: key},
              key: stringify({[groupName]: key}),
              id: safeKey,
              title: safeKey == EMPTY_STRING_VALUE ? strings.noValue :
                     safeKey === NULL_KEY_VALUE ? (grouping.fieldDefinition?.def?.nullLabel ?? strings.unassigned) : grouping?.fieldDefinition?.group?.titleFor ? grouping?.fieldDefinition.group.titleFor({keys: {[groupName]: key}}) : grouping?.def ? catalog.attrValueLabelFor({v: key, def: grouping?.def}) : safeKey,
              count: doc_count,
              offline_count,
              offlineItems,
              _source: groupData ? groupData.hits.hits[0]._source : [],
              aggs: rest
            })
          }
        }).filter(_.identity)
      } else {
        // Exists query - Yes/No - Has XYZ?

        const Yes = _.capitalize(grouping?.def ? catalog.attrValueLabelFor({v: true, def: grouping?.def}) : strings.yes),
          No = _.capitalize(grouping?.def ? catalog.attrValueLabelFor({v: false, def: grouping?.def}) : strings.no),
          offlineNo = groupedOfflineItems[stringify({[groupName]: false})] ?? [],
          offlineYes = groupedOfflineItems[stringify({[groupName]: true})] ?? []

        transformed = [
          (hasFilter ? filterGroup.selected.has(true) || filterGroup.selected.has('true') : !hasFilter) && (exists + offlineYes.length) > 0
          ? {
              key: stringify({...parentKeys, [groupName]: Yes}),
              keys: {[groupName]: true},
              id: 'true',
              title: Yes,
              count: exists,
              offline_count: offlineYes.length,
              offlineItems: offlineYes
            }
          : null,
          (hasFilter
           ? filterGroup.selected.has(false) || filterGroup.selected.has('false')
           : !hasFilter)
          && (itemCount + offlineNo.length) - exists > 0
          ? {
              key: stringify({...parentKeys, [groupName]: No}),
              keys: {[groupName]: false},
              id: 'false',
              title: No,
              count: itemCount - exists,
              offline_count: offlineNo.length, offlineItems: offlineNo
            } : null
        ].filter(_.identity)
        totalGroups = itemCount == 0 ? 0 : (itemCount - exists) % itemCount == 0 ? 1 : 2
        unassigned = 0
        if (grouping?.firstOrderBy.field === '_key') {
          transformed = _.orderBy(transformed, 'title', grouping?.firstOrderBy.order)
        }
        if (grouping?.firstOrderBy.field === '_count') {
          transformed = _.orderBy(transformed, 'count', grouping?.firstOrderBy.order)
        }
      }
    }

    //console.log({query: stringify(queryVariables), transformed, buckets, rest})

    if (this.grouping == grouping) {  // The user may have changed the grouping while we had an outstanding query (whose results are now invalid)
      const {grouping} = this
      if (grouping?.fieldDefinition?.group?.custom?.customSort) {
        try {
          transformed = grouping?.fieldDefinition.group.custom.customSort(transformed, {store: this.parentGroupStore, stores: this.stores})
        } catch (err) {
          onSiteError(err)
        }
      }

      runInAction(`Save results for group by ${grouping?.groupName}`, () => {
        const {timesClicked, rawData} = this
        //this.timesClicked = Math.min(Math.floor(totalGroups / itemsPerClick), timesClicked)
        Object.assign(this, {
          initialLoad: false,
          loading: false,
          itemCount,
          online_unassigned: unassigned,
          groupCount: totalGroups,
          lastGroupCount: totalGroups,
          //groupResults: [...store.groupResults, transformed] // expensive compared to push
        } as Partial<GroupByGroupsQueryManager>)
        timesClicked ? rawData.push(transformed) : rawData.replace([transformed])
      })
    }

    return transformed
  }

  @action maybeRunQueryDebounced = _.debounce(() => this.maybeRunQuery(), 150)
  @action maybeRunQuery = async () => {
    const {catalog, grouping, isExpanded, queryVariables, groupName, hasFilter, filterGroup} = this
    // DEBUG && console.log(`groupQueryManager.maybeRunQuery()`, {groupName, isExpanded})

    // console.trace()

    if (!authStore.userIsAuthenticated || !isExpanded || !queryVariables) {
      // DEBUG && console.log(`Skip run query for group by `, {isExpanded, queryVariables})
      return
    }

    if (grouping?.isCatalogItemsGrouping) return

    if (!grouping) {
      DEBUG && console.warn(`Unable to find grouping for ${this.groupName}`)
      return
    }

    if (grouping.isOfflineGroup) {
      const {itemCount, offlineItemCount} = catalog, {path, rawData} = this
      runInAction(() => {
        this.initialLoad = this.loading = false
        this.lastGroupCount = this.groupCount = 2
        this.itemCount = itemCount
        this.rawData.replace([
          [
            {key: true, title: 'Yes', count: 0, offline_count: offlineItemCount, id: true},
            {key: false, title: 'No', count: itemCount, offline_count: 0, id: false}
          ]
        ])

        grouping.unregisterNodes(path)
        grouping.registerChildren(rawData[0], path)
      })

      this.loading = false
      return
    }

    if (!grouping.fieldDefinition) {
      DEBUG && console.warn(`Unable to find field definition for ${this.groupName}`)
      return
    }

    if (grouping.isCatalogItemsGrouping) {
      DEBUG && console.warn(`Group is the product level group`)
      return
    }

    const {es, timesClicked, compositeClause} = this
    /** While switching seasons we need to wait for the total item count so we can pick our size */
    // For custom sorts we have to get every possible aggregate row to guarantee correct scoring
    // We don't want to render all of those cards as it's very slow, instead just increment the # of virtual rows on screen
    // Since we can't get total count for composite aggs, load everything up front and page through it client side
    if ((timesClicked > 0 && compositeClause && !_.isEmpty(grouping.customOrders)) || compositeClause?.composite?.size === 0) {
      this.loading = false
      DEBUG && console.log(`Ignoring run query`, {timesClicked, size: compositeClause?.composite?.size})
      return
    }
    /*maxItemsAlreadyLoaded < _.sumBy(rawData, d => d.length)) {
      return
    }*/

    // DEBUG && console.log(`Running ${this.toString()} children groups query`, {compositeClause: toJS(compositeClause), groups: toJS(this.groupsClause), grouping, path})
    if (grouping?.fieldDefinition) {
      this.loading = true

      try {
        const {queryVariablesBody: body} = this,
          {firstOrderBy, compositeFields, isNestedField, sectionConfig} = grouping

        let esResult: any = await es.search(
          {
            meta: `GroupByGroupsQueryManager.maybeRunQuery() - timesClicked: ${timesClicked} level: ${this.grouping.level} path: ${this.path.join(' → ')} groupName: ${this.groupName}`,
            index: catalog.index,
            batch: false,
            body
          },
          {
            log: {query: false, result: false}
          }
        )

        if (!esResult.aggregations) {
          this.initialLoad = this.loading = false
          DEBUG && console.warn(`No aggregations returned from query`, {body, esResult})
          return null
        }

        if (isNestedField) {
          esResult = {...esResult, aggregations: esResult.aggregations[sectionConfig.path]}
        }

        if (esResult?.aggregations?.filtered) {
          esResult = {...esResult, aggregations: esResult.aggregations.filtered}
        }

        return this.transformResults({esResult})
      } catch (err) {
        onSiteError(err)
      } finally {
        runInAction(() => {
          this.initialLoad = false
          this.loading = false
        })
      }
    }
  }

  // endregion

  /**
   * This can be a single expanded or collapsed row when in flat mode (or VLP), or can be a row with a bunch of groups in it when in Concept Board
   */
  @computed get totalRowCount() {
    const {grouping, expandedSize, groupCount, unassigned, catalog: {groupBy: {settings: {showOnlyExpanded, groupView}}}} = this
    if (!grouping) return 0

    let adjustedGroupCount = groupCount + (unassigned > 0 ? 1 : 0)
    const totalRowCount = showOnlyExpanded ? expandedSize
                                           : groupView == 'card' ? expandedSize + adjustedGroupCount : adjustedGroupCount

    // console.log({showOnlyExpanded, expandedSize, groupCount, unassignedCount, adjustedGroupCount, totalRowCount})

    return totalRowCount
  }

  private _lastPartitionedGroups: Array<IGroupByQueryManagerResult | Array<IGroupByQueryManagerResult>>

  @computed get visibleGroups() {
    const {catalog: {settings: {groupBy: {showEmptyGroups}}}} = this
    return this.partitionedGroups.filter(rowData => (!_.isArray(rowData) && (showEmptyGroups || rowData.count || rowData.offline_count)) || (_.isArray(rowData) && rowData.length))
  }

  /**
   * Partition collapsed groups together
   */
  @computed get partitionedGroups(): Array<IGroupByQueryManagerResult | Array<IGroupByQueryManagerResult>> {
    const {catalog: {groupBy: {settings: {groupView, showExpandedFirst, showOnlyExpanded}}}} = this,
      {loading, _lastPartitionedGroups, data, grouping, path} = this
    //partitioned: Array<IGroupAggregateData | Array<IGroupAggregateData>> = []

    if (loading) {
      return _lastPartitionedGroups
    } else {
      let result = data
      if (showOnlyExpanded) {
        result = result.filter(g => grouping.isExpanded(g.key, path))
      } else if (showExpandedFirst) {
        result = _.orderBy(data, g => !grouping.isExpanded(g.key, path))
        //console.log({data: toJS(data, {recurseEverything: true}), result: toJS(result, {recurseEverything: true})})
      }

      if (_.isEqual(result, _lastPartitionedGroups)) {
        return _lastPartitionedGroups
      }
      return result
    }

    // Too slow - rewrite - this is only needed for stacked group cards feature
    /*
    if (grouping.expanded.size == 0) {

    }
    for (var entry of data) {
      if (grouping.isExpanded(entry.id)) {
        partitioned.push(entry)
      } else if (!showOnlyExpanded) {
        const last = _.last(partitioned)
        if (_.isArray(last)) {
          last.push(entry)
        } else {
          partitioned.push([entry])
        }
      }
    }

    if (showExpandedFirst) {
      partitioned = _.orderBy(partitioned, p => _.isArray(p))
    }

    // console.log({data, partitioned})
    return partitioned*/
  }

  @computed get showOnlyExpandedMenu() {
    const {data, grouping, path} = this,
      result = data?.filter(g => grouping.isExpanded(g.key, path)).length
    return result ? true : false
  }

  /**
   * Our virtual index may land us on an expanded row or a row of collapsed cards
   */
  itemsForRowIndex: (index: number) => IGroupByQueryManagerResult | Array<IGroupByQueryManagerResult> = (rowIndex: number) => {
    if (!this.grouping) return null

    const {partitionedGroups} = this
    return rowIndex < partitionedGroups.length ? partitionedGroups[rowIndex] : null
    /*
    let index = rowIndex
    const {
        grouping: {isExpanded}, data, expandedGroups, totalRowCount, idDict, expandedSize
      } = this,
      {catalog: {stackView, groupBy: {showExpandedFirst, style: itemStyle}}} = this,
      cardsPerRow = 8

    let result = null

    if (showExpandedFirst) {
      if (index < expandedSize) {
        result = expandedGroups[index]
      } else if (itemStyle == 'card') {
        index -= expandedSize
        const skip = cardsPerRow * index
        const slice = data ? data.slice(skip, cardsPerRow + skip) : []

        result = slice
      }
      // Expanded items are moved to top of list and must be skipped when indexing internally
      else {
        if (expandedSize == 0) {
          result = [data[index]]
        } else {
          index -= expandedSize
          let expandedRemaining = expandedSize
          for (var i = 0; i < data.length; i++) {
            if (!isExpanded(data[i].id)) {
              if (index == 0) {
                result = [data[i]]
                break
              } else { index-- }
            } else {
              expandedRemaining--
              if (expandedRemaining == 0) {
                result = [data[i + index + 1]]
                break
              }
            }
          }
        }
      }
    }
    else {
      if (stackView) {
        const{partitionedGroups} = this
        console.log({partitionedGroups, totalRowCount, data, index, expandedGroups, expandedSize})
        return partitionedGroups[index]
      } else if (itemStyle == 'flat') {
        const item = data[index]
        result = item && isExpanded(item.id) ? item : [item]
      } else { // ysl card view - likely dead
        const expandedBeforeCurrentRow = []

        for (const edge of expandedGroups) {
          const {id} = edge
          const startIndex = index * cardsPerRow

          if (idDict[id].dataIndex < startIndex) {
            expandedBeforeCurrentRow.push(id)
            index--
          } else {
            break
          }
        }

        const skip = cardsPerRow * index
        const slice = data ? data.slice(skip, cardsPerRow + skip) : []

        const lastExpandedBeforeUs = _.last(expandedBeforeCurrentRow)

        if (lastExpandedBeforeUs && slice.find(({id}) => id == lastExpandedBeforeUs)) {
          result = idDict[lastExpandedBeforeUs]
        } else {
          result = slice
        }

        false && console.log({rowIndex, index, result, expandedBeforeCurrentRow})
      }
    }

    //console.log({virtualIndex, index, result})

    if (result == null && index < totalRowCount) {
      console.warn(`Could not find node(s) for row ${index} even though there should be data.`)
    }

    return result*/
  }

  // region Load More Behavior (User clicks a button to load more groups)

  @observable timesClicked = 0

  @computed get itemsPerClick() {
    return this.grouping?.isNestedField ? 10000 : 50
  }

  @computed get hasMore() {
    return this.remainingToLoad > 0
  }

  @computed get remainingToLoad() {
    const {timesClicked, itemsPerClick, totalRowCount} = this
    return Math.max(0, totalRowCount - (itemsPerClick * (timesClicked + 1)))
  }

  @action onClickLoadMore = () => {
    this.remainingToLoad && this.timesClicked++
  }

  @action onClickLoadMore_infinite = (page: number) => {
    console.log(`onClickLoadMore_infinite(${page})`, this.remainingToLoad)
    // Delay this to give time to show the loader
    if (this.remainingToLoad) {
      this.loading = true
      setTimeout(() => {
        try {
          this.timesClicked++
          this.maybeRunQueryDebounced()
        } catch (err) {
          onSiteError(err)
          this.timesClicked--
          debugger
        }
      }, 50)
    }
  }

  // endregion

  @computed get validExpanded() {
    if (!this.grouping) { return [] }
    const {grouping: {expanded}, idDict} = this

    return Array.from(expanded.keys()).filter(id => idDict[id])
  }

  @computed get expandedSize() {
    return this.validExpanded.length
  }

  @computed get idDict(): { [id: string]: { node: { id }, dataIndex: number } } {
    return this.data.reduce((acc, v, i) => ({...acc, [v.id]: {...v, dataIndex: i}}), {})
  }

  _toDispose = []

  @computed get isExpanded() {
    const {level, path, catalog: {isShowingClientPDF, groupBy: {isExpandedTo}}} = this
    return isShowingClientPDF || level == 0 || isExpandedTo(path)
  }

  registerEffects = () => {
    const {strings, catalog, filter} = this
    this._toDispose.push(
      autorun(() => {
        if (this.grouping) {
          this._toDispose.slice(1).forEach(f => f())

          this._toDispose.push(
            autorun(() => {
              const {grouping, loading} = this
              // trace()
              // console.log({loading, grouping})

              if (grouping && !loading) {
                const {rawData} = this
                const lastRawData = _.last(rawData),
                  flatRawData = _.flatMap(rawData)

                runInAction(() => {
                  const {timesClicked, allGroups, unassigned, path} = this
                  // console.log({grouping, timesClicked, allGroups, rawData, unassignedCount, path, loading})
                  !timesClicked && grouping.unregisterNodes(path)

                  const dict = _.keyBy(flatRawData, t => t.keys[grouping.esField]),
                    emptyGroups = allGroups.filter(g => !dict[g.key])

                  grouping.registerChildren([...(lastRawData || []), unassigned && {id: JSON.stringify({[grouping.groupName]: null}), label: grouping.fieldDefinition?.def?.nullLabel ?? strings.unassigned}, ...emptyGroups].filter(_.identity), path)
                })
              }
            }, {name: `Register group children for ${this.groupName} - ${_.isEmpty(this.path) ? 'root' : this.path.join(', ')}`}),
            reaction(
              () => catalog.seasonSelector.dirty,
              (dirty) => {
                if (!dirty) {
                  this.initialLoad = true
                  this.timesClicked = 0
                }
              },

              {name: 'Show loader when navigating the hierarchy'}
            ),
            reaction(
              () => this.partitionedGroups,
              () => this._lastPartitionedGroups = this.partitionedGroups,
              {name: 'Save off previous groups to use during load'}
            ),
            reaction(
              () => {
                try {
                  // Dependencies
                  // trace()
                  if (!this.grouping?.fieldDefinition) return null

                  const {groupName, grouping: {functionScoreTerm, orderBy, fieldDefinition: {group: {subtitle, title}}}, path, compositeClause, itemsPerClick, catalog: {es: {refetchId}, queryVariablesEs, groupBy: {hierarchy}}, vlp: {hierarchy: vlpHierarchy}} = this
                  let collections, folders, plans
                  if (vlpHierarchy) {
                    collections = vlpHierarchy.selected.collections
                    folders = vlpHierarchy.selected.folders
                    plans = vlpHierarchy.selected.plans
                  }
                  // trace()
                  return {groupName, functionScoreTerm, ...orderBy, subtitle, title, path, compositeClause, itemsPerClick, refetchId, collections, folders, plans}
                } catch (err) {
                  return undefined
                }
              },
              action(() => {
                this.after_key = undefined
                this.timesClicked = 0
                this.online_unassigned = 0
                this.lastGroupCount = this.groupCount
                this.groupCount = 0
              }), {name: `Reset query manager data`}),

            reaction(() => {
              //trace()
              return this.initialLoad ? this.isExpanded : false
            }, (isExpanded) => {
              if (this.initialLoad) {
                this.maybeRunQueryDebounced()
              }
            }, {name: `Run query when group is first expanded`}),
            autorun(() => {
              const {filtersWithSelections} = filter
              // Piyush Bhati - Fix for bug-YFTS-7399 - timesClicked is not resetting when filter get applied
              // so added new autorun to reset timesClicked when filtersWithSections changes
              this.timesClicked = 0
            }),
            autorun(() => {
              //trace()
              // todo - perf - separate the building of the Pending Items groups from ones that require querying ES - currently when we add/duplicate an item (create a new Pending Item) we rerun the query and regenerate all the cards
              const {groupsClause, level, compositeClause, timesClicked, grouping, path, groupedOfflineItems} = this,
                {es: {refetchId}, groupBy: {hierarchy}, seasonSelectors, itemCount} = catalog,
                {filtersWithSelections} = filter,
                {vlp: {hierarchy: {selected}}} = this

              if (catalog.hasLoadedSeasons && grouping) {
                const {fieldDefinition, groupName, functionScoreTerm, firstOrderBy: {field, order, custom}} = grouping
                if (fieldDefinition) {
                  const {group} = fieldDefinition
                  const deps = [group?.title, group?.subtitle]
                }

                // Requery if the hierarchy changes and we're grouped by a hierarchy field
                if (groupName.startsWith('VLP_')) {
                  const {vlp: {hierarchy: {selected: {plans, folders, collections}}}} = this
                }

                // We don't want to run the query when we're collapsed, but we also don't want to rerun the query when the user collapses and then reexpands
                runInAction(() => {
                  this.loading = true
                  this.maybeRunQueryDebounced()
                })
              }
            }, {name: `Run group by query for ${this.path.join(' → ')}${this.grouping?.groupName}`}),

            autorun(async () => {
              const {catalog: {settings: {groupBy: {showEmptyGroups}}}} = this,
                {grouping} = this
              if (showEmptyGroups) {
                if (grouping) {
                  const {findAllGroups_trigger} = grouping
                }

                await this.findAllGroups()
              } else {
                this.allGroups = []
              }
            }))
        } else {
          runInAction(() => {
            this.loading = false
          })
        }
      }))

    return () => {
      this._toDispose.forEach(f => f())
    }
  }

  @action findAllGroups = async () => {
    const {grouping, parentGroupStore} = this

    if (!grouping) return null

    this.allGroups = await grouping.findAllGroups(parentGroupStore)
  }

  @observable.ref allGroups: Array<{ key: string, id: string, title: any, keys: { [key: string]: any } }> = []

  @computed get allGroupsByKey() {
    return _.keyBy(this.allGroups, g => g.key)
  }

  dispose = () => {
    this._toDispose.forEach(f => f())
  }
}




// WEBPACK FOOTER //
// ./src/modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/GroupByCatalog_Groups/GroupByCatalog_LoadMore/GroupByGroupQueryManagerStore.tsx